<template>
    <div>
        <nav
            class="navbar navbar-expand navbar-dark border-bottom overlay-body"
            :class="[navbarFixed, {drawerActive: active}]"
            :style="navStyle"
        >
            <div v-if="stripeColor"
                class="nch--navbar-stripe"
                :style="{ background: stripeColor }"
            ></div>
            <Drawer
                @closeDrawer="activate"
                @login="login"
                :languages="languages"
                :menuItems="customMenu ? reverseListMenu : false"
                :user="user"
                :active="active"
                :glossary-link="glossaryLink"
                :publicUser="publicUser"
                :loginSocial="loginSocial"
                :gamificationRules="gamificationRules"
            />
            <div :class="[layout.navContainer ? 'container' : 'container-fluid', navbarDirection]">
                <div class="nch--brand-group">
                    <a class="navbar-brand text-white" href="/">
                        <div v-if="logo.type">
                            <img class="nch--nav-brand" :src="image(logo.src)" alt="logo" />
                        </div>
                        <h4
                            :class="$mq === 'sm' ? 'overflow mb-0' : 'mb-0'"
                            v-else
                            :style="{color: navColor.secondary}"
                        >{{ logo.text}}</h4>
                    </a>
                    <a
                        v-if="secondLogoSrc"
                        class="navbar-brand nch--brand-second"
                        :href="layout.second_logo_url || '#'"
                        :target="layout.second_logo_blank ? '_blank' : '_self'"
                        :rel="layout.second_logo_blank ? 'noopener noreferrer' : null"
                    >
                        <img class="nch--nav-brand" :src="image(secondLogoSrc)" alt="logo secundária" />
                    </a>
                </div>

                <template v-if="user">
                    <ul v-if="$mq !== 'lg'" class="navbar-nav align-items-center">
                        <NotificationsMenu
                            class="link"
                            mobile
                            v-if="!publicUser"
                            :user="user"
                            :notifications="notifications"
                            :notification-count="notificationCount"
                            :color="navColor"
                        />
                        <Menu class="link dashboard-menu" :user="user" :items="menuItems" />
                        <i
                            @click="activate"
                            @keydown.enter.prevent="activate"
                            @keydown.space.prevent="activate"
                            role="button"
                            tabindex="0"
                            :aria-expanded="active ? 'true' : 'false'"
                            aria-label="Abrir menu"
                            :style="{color: navColor.secondary}"
                            class="mdi mdi-menu ni-2x cursor-pointer"
                        ></i>
                        <Login
                            mobile
                            :disableRegister="disableRegister"
                            class="ml-3"
                            :color="navColor"
                            :hasForm="hasForm"
                            :hasLogin="hasLogin"
                            :hubHash="hubHash"
                        />
                    </ul>
                    <!-- Menus usuário logado -->
                    <template v-else>
                        <ul
                            class="navbar-nav align-items-left ml-0 nch-main-menu"
                            :class="this.layout.position ? 'mr-auto' : 'ml-auto'"
                        >
                            <CustomMenu
                                class="link"
                                :menu="reverseListMenu"
                                :user="user"
                                :publicUser="publicUser"
                            />
                            <NotificationsMenu
                                class="link notifications-menu"
                                v-if="!publicUser"
                                :user="user"
                                :notifications="notifications"
                                :notification-count="notificationCount"
                                :color="navColor"
                            />
                            <template v-if="!customMenu">
                                <Cart class="link" :cart="cart" :color="navColor" />
                                <a
                                    class="nav-link link"
                                    href="/plans"
                                    role="button"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                    :style="{color: navColor.secondary}"
                                >
                                   <i class="fas fa-store mx-2" :style="{color: navColor.secondary}">
                                   </i>
                                    <span class="font-weight-bold">{{ $t('common.store') }}</span>
                                </a>
                            </template>
                            <NavbarDashboard
                                v-if="!publicUser && !customMenu"
                                class="link"
                                :gamificationRules="gamificationRules"
                            />
                            <!-- Glossário (backlog#342): ao lado de Planos / Meus planos, quando ligado e com termo ativo -->
                            <li v-if="!publicUser && glossaryLink" class="nav-item link nch--glossary-link">
                                <a class="nav-link font-weight-bold" :href="glossaryLink" :style="{color: navColor.secondary}">
                                    <i class="fas fa-book-open mr-1" :style="{color: navColor.secondary}"></i>{{ $t('glossary.menu') }}
                                </a>
                            </li>
                            <Menu
                                class="link dashboard-menu"
                                :user="user"
                                :color="navColor"
                                :items="menuItems"
                            />
                            <Language :languages="languages" />
                            <!-- usuário público logado -->
                            <template v-if="publicUser">
                                <navbar-public-user class="link"></navbar-public-user>
                                <Login
                                    v-if="publicUser"
                                    class="ml-3"
                                    :color="navColor"
                                    :hasForm="hasForm"
                                    :hasLogin="hasLogin"
                                    :hubHash="hubHash"
                                />
                            </template>
                        </ul>
                        <ul v-if="!publicUser" class="navbar-nav align-items-center">
                            <Profile :color="navColor" :user="user" :wiki-enabled="wikiEnabled" />
                        </ul>
                    </template>
                </template>
                <template v-else>
                    <div v-if="$mq !== 'lg'" class="menu">
                        <Menu class="link dashboard-menu" :user="user" :items="menuItems" />
                        <i
                            @click="activate"
                            @keydown.enter.prevent="activate"
                            @keydown.space.prevent="activate"
                            role="button"
                            tabindex="0"
                            :aria-expanded="active ? 'true' : 'false'"
                            aria-label="Abrir menu"
                            :style="{color: navColor.secondary}"
                            class="mdi mdi-menu ni-2x cursor-pointer"
                        ></i>
                        <Login
                            mobile
                            :disableRegister="disableRegister"
                            class="ml-3"
                            :color="navColor"
                            :hasForm="hasForm"
                            :hasLogin="hasLogin"
                            :hubHash="hubHash"
                        />
                    </div>
                    <div v-else>
                        <!-- Menus usário não autenticado -->
                        <ul class="navbar-nav align-items-center ml-auto ml-0">
                            <CustomMenu
                                class="link"
                                :menu="reverseListMenu"
                                :user="user"
                                :publicUser="publicUser"
                            />
                            <template v-if="!customMenu">
                                <Cart class="link" :cart="cart" :color="navColor" />
                                <a
                                class="nav-link link"
                                href="/plans"
                                role="button"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                :style="{color: navColor.secondary}"
                                >
                                    <i class="fas fa-store mx-2" :style="{color: navColor.secondary}">
                                    </i>
                                    <span class="font-weight-bold">{{ $t('common.store') }}</span>
                                </a>
                            </template>

                            <Menu class="link dashboard-menu" :user="user" :items="menuItems" />

                            <Language :languages="languages" />
                            <Login
                                :disableRegister="disableRegister"
                                class="ml-3"
                                :color="navColor"
                                :hasForm="hasForm"
                                :hasLogin="hasLogin"
                                :loginSocial="loginSocial"
                                :hubHash="hubHash"
                            />
                        </ul>
                    </div>
                </template>
            </div>
        </nav>
    </div>
</template>

<script>
import BaseButton from '../argon/components/BaseButton';
import image from '../mixins/image';

import NotificationsMenu from './NavbarComponents/NavbarNotificationsMenu';
import NavbarMobile from './NavbarComponents/NavbarMobile';
import Language from './NavbarComponents/NavbarLanguage';
import Profile from './NavbarComponents/NavbarProfile';
import Login from './NavbarComponents/NavbarLogin';
import Menu from './NavbarComponents/NavbarMenu';
import Cart from './NavbarComponents/NavbarCart';
import Drawer from '../components/Common/Drawer';
import CustomMenu from './NavbarComponents/NavbarCustomMenu';
import NavbarPublicUser from './NavbarComponents/NavbarPublicUser';
import NavbarDashboard from './NavbarComponents/NavbarDashboard';

export default {
    name: 'Navbar',
    mixins: [image],
    props: {
        color: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
        },
        logo: {
            type: Object,
            required: true,
        },
        cart: {
            type: Number,
            required: true,
        },
        notifications: {
            type: Array,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        layout: {
            type: Object,
            required: true,
        },
        documents: {
            type: Array,
        },
        disableRegister: {
            type: Boolean,
            default: false,
        },
        menuItems: {
            type: [Object, Array],
            required: true,
        },
        publicUser: [Boolean, Number],
        hasForm: [Boolean, Number],
        hasLogin: Boolean,
        loginSocial: Array,
        hubHash: String,
        customMenu: Array,
        notificationCount: Object,
        clientSettings: String,
        settings: String,
        gamificationRules: {
            type: Object,
            required: false,
        },
        /** Glossário (backlog#342): endereço do link no menu, ou null para não mostrar */
        // Wiki colaborativa ligada no painel God (backlog#343)
        wikiEnabled: {
            type: Boolean,
            default: false,
        },
        glossaryLink: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            headerSize: {},
            active: false,
        };
    },
    components: {
        NotificationsMenu,
        NavbarMobile,
        Language,
        Profile,
        Login,
        Menu,
        Cart,
        Drawer,
        BaseButton,
        CustomMenu,
        NavbarPublicUser,
        NavbarDashboard,
    },
    methods: {
        setDataToStore() {
            this.$store.commit('SET_NAVBAR_POSITION', this.layout.position);
            this.$store.commit('SET_NAVBAR_FIXED', this.layout.fixed);
            this.$store.commit('SET_COLOR', this.color);
            this.$store.commit('SET_NAV_TEXT_FOLLOW', this.layout.nav_text_follow_page);

            if (this.documents) {
                this.$store.commit('SET_DOCUMENTS', this.documents);
            }
            if (this.user) {
                this.$store.commit('SET_PUBLIC_USER', this.publicUser);
                this.$store.commit('SET_USER_ID', this.user.id);
                this.$store.commit('SET_USER_NAME', this.user.name);
            }
        },
        activate() {
            this.active = !this.active;
        },
        login() {
            console.log(this.$refs);
        },
    },
    computed: {
        navbarFixed() {
            if (this.layout.fixed) return 'fixed-top';
        },
        navStyle() {
            // A inline `position: relative` antiga sobrescrevia o `position: fixed`
            // da classe `fixed-top` (inline vence classe) → menu nunca fixava.
            // Agora a position é dinâmica: fixed quando habilitado, relative caso
            // contrário (necessária para o filete/stripe absoluto).
            return {
                ...this.$bgColor(this.color.primary),
                minHeight: '95px',
                paddingBottom: '4px',
                position: this.layout.fixed ? 'fixed' : 'relative',
            };
        },
        navbarDirection() {
            if (this.layout.position) return 'revert';
        },
        secondLogoSrc() {
            // Retorna o valor cru (objeto de upload ou string URL); o mixin
            // `image()` resolve ambos os formatos.
            return this.layout && this.layout.second_logo ? this.layout.second_logo : null;
        },
        navColor() {
            // Cor efetiva (texto pode acompanhar a página com auto-contraste).
            return this.$store.getters.NAV_COLOR || this.color;
        },
        stripeColor() {
            // Toggle desliga o filete por completo.
            if (this.layout.stripe_enabled === false) {
                return null;
            }
            // Quando "acompanhar a página" está ativo, o filete usa a cor da
            // página atual (custom page); na home (sem PAGE_COLOR) cai na cor
            // definida pra barra, com fallback p/ a cor primária.
            if (this.layout.stripe_follow_page) {
                return this.$store.getters.PAGE_COLOR || this.layout.stripe_color || this.color.primary;
            }
            return this.layout.stripe_color || this.color.primary;
        },
        reverseListMenu() {
            if (this.customMenu) {
                const reverseCustomMenu = [...this.customMenu];
                return reverseCustomMenu.reverse();
            }
            return null;
        },
    },
    mounted() {
        this.setDataToStore();
        this.$store.commit('SET_CART_ITEMS', this.cart);
    },
    created() {
        if (!this.$getSettings) {
            this.$createSettings(JSON.parse(this.clientSettings), JSON.parse(this.settings));
        }
    },

};
</script>

<style>
.cursor-pointer {
    cursor: pointer;
}

.nch--nav-brand {
    height: 60px !important;
    max-height: 60px !important;
    object-fit: contain;
    flex-shrink: 0;
}

/* Grupo de logos (principal + secundária/Câmara) separadas */
.nch--brand-group {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
}
.nch--brand-second {
    display: flex;
    align-items: center;
    padding-left: 16px;
    border-left: 1px solid hsla(0, 0%, 100%, 0.25);
    margin: 0;
}

.border-bottom {
    border-color: hsla(0, 0%, 100%, 0.08) !important;
}

.nch--navbar-stripe {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    pointer-events: none;
}

.revert {
    flex-direction: row-reverse;
}

.overflow {
    width: 15em;
    margin: 0 0 2em 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.8rem;
    line-height: 1;
}
.menu{
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 10px;
}
.nch-main-menu {
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>

